import QRCode from 'qrcode';
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { Driver, DriverLink } from '../types';
import Dialog from './Dialog';

interface Props {
  drivers: Driver[];
  /** Osvežava spisak posle pravljenja linka, da se vidi da ga vozač ima. */
  onChanged: () => void;
}

/**
 * Linkovi kojima vozači otvaraju svoju turu.
 *
 * Vozač se ne prijavljuje: telefon radi bez mreže, a kucanje lozinke po
 * hladnoći nije posao koji treba tražiti od čoveka koji nosi veš mašinu. Zato
 * pristup nosi link — ali link koji se ne može pogoditi.
 *
 * Ključ se prikazuje SAMO jednom, odmah po pravljenju. Ne čuva se za kasnije
 * gledanje: kad zatreba ponovo, pravi se nov, a stari time prestaje da važi —
 * što je upravo ono što treba kad se telefon izgubi ili vozač ode.
 */
export default function DriverLinks({ drivers, onChanged }: Props) {
  const [fresh, setFresh] = useState<DriverLink | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Vozač kome se pravi nov link, kad stari treba ugasiti. */
  const [replacing, setReplacing] = useState<Driver | null>(null);

  const create = async (driver: Driver) => {
    setReplacing(null);
    setBusy(driver.id);
    try {
      const link = await api.createDriverLink(driver.id);
      setFresh(link);
      setError(null);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const fullLink = fresh ? `${window.location.origin}${fresh.path}` : '';

  /**
   * Kvadratić koji vozač skenira kamerom.
   *
   * Link je trideset dva nasumična znaka — ne prekucava se, a diktiranje preko
   * telefona je siguran način da se pogreši. Vozač ionako svako jutro dolazi po
   * turu, pa je kamera najkraći put: uperi, otvori, gotovo.
   *
   * Crta se ovde, u pregledaču. Ključ tako ne odlazi nijednom servisu za
   * pravljenje kodova — a poslao bi mu upravo ono što treba da ostane tajna.
   */
  const qr = useRef<HTMLCanvasElement | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!fresh || !qr.current) {
      return;
    }

    void QRCode.toCanvas(qr.current, fullLink, { width: 220, margin: 1 });
  }, [fresh, fullLink]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Bez dozvole za ostavu ostaje polje iz kog se kopira rukom.
      setError('Pregledač nije dao da se kopira — označi link u polju pa Ctrl+C.');
    }
  };

  // Link se sklapa od adrese na kojoj operater trenutno radi. Kad je to
  // localhost, telefon ga ne može otvoriti — localhost je za njega on sam.
  const localOnly = window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1';

  return (
    <section className="panel">
      <h2>Linkovi vozača</h2>

      <p className="note">
        Vozač otvara turu ovim linkom, bez prijavljivanja — telefon mu radi i bez
        signala. Link se vidi samo jednom, odmah po pravljenju; kad zatreba
        ponovo, napravi nov, a stari time prestaje da važi.
      </p>

      {error && <div className="health health--bad">{error}</div>}

      {fresh && (
        <div className="health health--ok">
          <strong>{fresh.fullName}</strong> — daj mu turu na jedan od tri načina:

          <div className="qr">
            <div className="qr__code">
              <canvas ref={qr} />
              <span className="muted">Skenira kamerom telefona</span>
            </div>

            <div className="qr__rest">
              <input
                className="driverlink__value"
                readOnly
                value={fullLink}
                onFocus={(e) => e.target.select()}
              />

              <div className="actions">
                <button type="button" onClick={() => void copy()}>
                  {copied ? 'Kopirano' : 'Kopiraj link'}
                </button>

                {/* Viber je ono što vozači ionako imaju. Otvara se sa spremnom
                    porukom; kome ide bira operater, jer telefon vozača ne mora
                    biti isti kao broj u šifarniku. */}
                <a
                  className="qr__share"
                  href={`viber://forward?text=${encodeURIComponent(
                    `Tura za danas: ${fullLink}`)}`}
                >
                  Pošalji Viberom
                </a>
              </div>

              <p className="note">
                Link se vidi samo sada. Kad zatreba ponovo, napravi nov — stari
                time prestaje da važi, pa se izgubljen telefon rešava jednim
                potezom.
              </p>
            </div>
          </div>

          {localOnly && (
            <p className="note">
              Ovaj link vodi na <code>localhost</code>, a to je za telefon — on
              sam. Otvori ovaj ekran preko adrese računara u mreži (npr.
              <code> http://192.168.1.43:3000</code>) pa napravi link ponovo, ili
              u linku zameni <code>localhost</code> tom adresom.
            </p>
          )}
        </div>
      )}

      <ul className="rows">
        {drivers.map((driver) => (
          <li key={driver.id}>
            <span className="row__name">
              {driver.fullName}
              {driver.phone && <span className="muted"> · {driver.phone}</span>}
            </span>

            <span className="row__side">
              <span className={driver.hasLink ? 'flag flag--ok' : 'flag flag--warn'}>
                {driver.hasLink ? 'ima link' : 'bez linka'}
              </span>

              <button
                type="button"
                disabled={busy === driver.id}
                onClick={() => (driver.hasLink ? setReplacing(driver) : void create(driver))}
              >
                {driver.hasLink ? 'Novi link' : 'Napravi link'}
              </button>
            </span>
          </li>
        ))}
      </ul>

      {replacing && (
        <Dialog
          title={`${replacing.fullName} već ima link`}
          description={
            <>Nov link gasi stari — telefon na kom je otvoren stari više neće moći
            da učita turu. Tako se izgubljen telefon rešava jednim potezom.</>
          }
          confirmLabel="Napravi nov link"
          danger
          busy={busy === replacing.id}
          onCancel={() => setReplacing(null)}
          onConfirm={() => void create(replacing)}
        />
      )}
    </section>
  );
}
